import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { SOLVED } from "../../../functions/constants";
import { fetchTicketsAsync } from "../ticketApi";
import { TICKET_STATES } from "../ticketConstants";
import { selectCurrentUser } from "../../user/userActions";

// Helpers / filters (kept for compatibility)
export const filterUnresolvedTickets = (ticket) =>
  ticket.processingState !== SOLVED;

// Async thunk using createAsyncThunk
export const fetchTicketsByEstateIdThunk = createAsyncThunk(
  "tickets/fetchTicketsByEstateId",
  async (estateId, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const { filteredServiceTypeIds } = selectCurrentUser(state);
      const ticketListFilter = selectTicketListFilter(state);
      const filter = { estateId };
      if (filteredServiceTypeIds && filteredServiceTypeIds.length > 0) {
        filter.serviceTypeIds = filteredServiceTypeIds;
      }

      if (ticketListFilter && ticketListFilter.length > 0) {
        filter.processingStates = ticketListFilter;
      }

      const response = await fetchTicketsAsync({
        params: {
          sort: `["updatedAt","DESC"]`,
          filter,
        },
      });

      if (response && response.data) {
        const filteredTickets = response.data.filter(filterUnresolvedTickets);
        return filteredTickets;
      }

      return [];
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

// Available filter ids (for reference):
// [
//   { id: "ALL", name: "Kein Filter" },
//   { id: "NEW", name: "Offen" },
//   { id: "ACCEPTED", name: "Freigegeben" },
//   { id: "IN_PROGRESS", name: "In Bearbeitung" },
//   { id: "WORK_DONE", name: "Ausgeführt" },
//   { id: "SOLVED", name: "Abgeschlossen" }
// ];

// Slice: use items/status/error like postingListSlice, preserve exported actions
const initialState = {
  items: [],
  status: "idle", // 'idle' | 'loading' | 'succeeded' | 'failed'
  error: null,
  filterItems: [
    TICKET_STATES.IN_PROGRESS,
    TICKET_STATES.NEW,
    TICKET_STATES.WORK_DONE,
  ],
};

const ticketsSlice = createSlice({
  name: "ticketList",
  initialState,
  reducers: {
    createTicket: (state, action) => {
      state.items = [action.payload, ...state.items];
    },
    updateTicket: (state, action) => {
      const idx = state.items.findIndex((t) => t.id === action.payload.id);
      if (idx !== -1) state.items[idx] = action.payload;
    },
    setTicketListFilter(state, action) {
      if (action.payload === "ALL") {
        state.filterItems = [
          TICKET_STATES.IN_PROGRESS,
          TICKET_STATES.NEW,
          TICKET_STATES.WORK_DONE,
        ];
        return;
      }
      state.filterItems = [action.payload];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTicketsByEstateIdThunk.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchTicketsByEstateIdThunk.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.items = action.payload || [];
      })
      .addCase(fetchTicketsByEstateIdThunk.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error;
      });
  },
});

export const { createTicket, updateTicket, setTicketListFilter } =
  ticketsSlice.actions;
export const ticketsReducer = ticketsSlice.reducer;

// Selector: normalize tickets list shape and always return an array of items
export const selectTicketItems = (state) => {
  const list = state && state.tickets && state.tickets.ticketsList;
  if (!list) return [];
  // support legacy (array) or new shape { items, status, error }
  return Array.isArray(list) ? list : list.items || [];
};

export const selectTicketListFilter = (state) => {
  return state && state.tickets && state.tickets.ticketsList.filterItems;
};

// Selector: return the raw ticketsList slice (or null)
export const selectTicketsSlice = (state) =>
  (state && state.tickets && state.tickets.ticketsList) || null;

// Selector: return status ('idle'|'loading'|'succeeded'|'failed')
export const selectTicketsStatus = (state) => {
  const slice = selectTicketsSlice(state);
  if (!slice) return "idle";
  return slice.status || "idle";
};

// Selector: return error from tickets slice
export const selectTicketsError = (state) => {
  const slice = selectTicketsSlice(state);
  return (slice && slice.error) || null;
};
